import { Component, NgZone, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from '../../../shared/services/api.service';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';

@Component({
  standalone: false,
  selector: 'app-provider',
  templateUrl: './provider.component.html',
  styleUrl: './provider.component.scss'
})
export class ProviderComponent implements OnInit {

  count = 0;
  dataObj: any = {};
  modalRef: any;
  gridApi: any;
  apiUrl: any;
  type = 'provider';
  isViewMode = false;
  providerTypes: any = [
    { id: 'SMS', name: 'SMS' },
    { id: 'EMAIL', name: 'Email' }
  ];
  countries: any;
  @ViewChild('content') content!: TemplateRef<any>;

  columnDefs: ColDef[] = [
    {
      headerName: 'Action',
      field: 'action',
      width: 200,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.gap = '5px';
        
        // Check if params.data exists
        if (!params.data) {
          return container; // Return empty container if no data
        }
        
        
        // Edit button
        const editButton = document.createElement('button');
        editButton.classList.add('btn', 'btn-sm', 'btn-primary');
        editButton.innerHTML = '<i class="bi bi-pencil"></i>';
        editButton.title = 'Edit';
        editButton.addEventListener('click', () => this.onEdit(params));
        container.appendChild(editButton);
        
        // Toggle status button
        const statusButton = document.createElement('button');
        const isActive = params.data && params.data.isActive !== undefined ? params.data.isActive : false;
        statusButton.classList.add('btn', 'btn-sm', isActive ? 'btn-warning' : 'btn-success');
        statusButton.innerHTML = `<i class="bi bi-${isActive ? 'toggle-on' : 'toggle-off'}"></i>`;
        statusButton.title = isActive ? 'Deactivate' : 'Activate';
        statusButton.addEventListener('click', () => this.toggleStatus(params.data));
        container.appendChild(statusButton);
        
        return container;
      },
    },
    {
      headerName: 'Status',
      field: 'isActive',
      editable: false,
      filter: true,
      cellRenderer: (params: any) => {
        const isActive = params.value !== undefined ? params.value : (params.data && params.data.isActive);
        const status = isActive ? 'Active' : 'Inactive';
        const badgeClass = isActive ? 'badge bg-success' : 'badge bg-danger';
        const span = document.createElement('span');
        span.className = badgeClass;
        span.textContent = status;
        return span;
      }
    },

    {
      headerName: 'Provider Name',
      field: 'providerName',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Type',
      field: 'providerType',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Country',
      field: 'countryCode',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Priority',
      field: 'priority',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Configuration',
      field: 'config',
      editable: false,
      filter: true,
      cellRenderer: (params: any) => {
        if (!params.data || !params.data.config) {
          return 'No config';
        }
        
        // Format config for display (show keys or first few key-value pairs)
        const config = params.data.config;
        if (typeof config === 'object' && config !== null) {
          const keys = Object.keys(config);
          if (keys.length === 0) {
            return '{}';
          }
          
          // Show first 2 keys with their values (masked for sensitive data)
          const visibleKeys = keys.slice(0, 2);
          let displayText = '{ ';
          visibleKeys.forEach((key, index) => {
            const value = config[key];
            let displayValue = typeof value === 'string' ? `"${value.substring(0, Math.min(8, value.length))}${value.length > 8 ? '...' : ''}"` : JSON.stringify(value);
            
            // Mask sensitive fields
            const sensitiveFields = ['apiKey', 'secretKey', 'password', 'token', 'auth'];
            if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
              displayValue = '"***"';
            }
            
            displayText += `"${key}": ${displayValue}`;
            if (index < visibleKeys.length - 1) {
              displayText += ', ';
            }
          });
          
          if (keys.length > 2) {
            displayText += `, ... (${keys.length - 2} more)`;
          }
          displayText += ' }';
          
          return displayText;
        }
        
        // If it's not an object, return as string
        return String(config);
      }
    },
  ];

  gridOptions: any = {
    paginationPageSize: 15,
    cacheBlockSize: 15,
    pagination: true,
    rowModelType: 'infinite',
    paginationPageSizeSelector: [15, 20, 50, 100],
    datasource: this.createDataSource(),
    rowSelection: { mode: 'multiRow' },
    context: {
      componentParent: this,
    },
  };

  constructor(
    private _modal: NgbModal,
    private _api: ApiService,
    private zone: NgZone
  ) { }

  ngOnInit(): void {
    const vm = this;
    vm.getCountries();
  }

  onGridReady(params: any) {
    const vm = this;
    vm.gridApi = params.api;
    if (vm.gridApi) {
      vm.gridApi = params.api;
      vm.gridApi.sizeColumnsToFit();
    }
  }

  refreshGrid() {
    const vm = this;
    if (vm.gridApi) {
      vm.gridApi.refreshInfiniteCache();
    }
  }

  createDataSource() {
    const vm = this;
    const datasource = {
      getRows: (params: any) => {
        // API endpoint for provider management
        vm.apiUrl = '/admin/providers';
        
        // Build query parameters - since the backend provider API doesn't use the same pagination
        // format as the legacy system, we'll call without pagination first
        vm._api.getData(vm.apiUrl).subscribe((res: any) => {
          if (res && !res.error) {
            // Response structure: { error: false, data: [...], message: "..." }
            const allData = res.data || [];
            
            // For infinite scrolling simulation, slice the data based on requested range
            const startRow = params.startRow;
            const endRow = startRow + vm.gridOptions.paginationPageSize;
            const pageData = allData.slice(startRow, endRow);
            
            // Set total count for pagination
            vm.count = allData.length;
            console.log('Total providers:', vm.count);
            console.log('Page data:', pageData);
            
            // Success callback with data and total count
            params.successCallback(pageData, vm.count);
          } else {
            console.error('API Error:', res);
            // If there's an error, return empty data but keep the grid functional
            params.successCallback([], 0);
          }
        }, (error) => {
          console.error('HTTP Error:', error);
          // On HTTP error, return empty data but keep the grid functional
          params.successCallback([], 0);
        });
      },
    };
    return datasource;
  }

  getCountries() {
    const vm = this;
    // Use the same pattern as other components
    vm._api.getData('/admin/get-data?type=country&_limit=500').subscribe((res: any) => {
      if (res && !res.error) {
        vm.countries = res.data;
      }
    });
  }

  addNew() {
    const vm = this;
    vm.dataObj = {
      providerName: '',
      providerType: '',
      countryCode: '', // Empty string corresponds to "Global Provider" option
      priority: 1,
      isActive: true,
      config: '{}'  // Initialize with empty JSON object as string
    };
    vm.isViewMode = false; // Set to edit mode
    vm.modalRef = vm._modal.open(this.content, { backdrop: 'static', size: 'xl' });
  }

  onEdit(data: any) {
    const vm = this;
    vm.zone.run(() => {
      vm.dataObj = { ...data.data }; // Create a copy to avoid direct mutation
      
      // Convert config object to JSON string for editing
      if (vm.dataObj.config && typeof vm.dataObj.config === 'object') {
        vm.dataObj.config = JSON.stringify(vm.dataObj.config, null, 2);
      }
      
      // Handle country code mapping - if countryCode is "GLOBAL" or falsy, set to empty string for the dropdown
      if (!vm.dataObj.countryCode || vm.dataObj.countryCode === 'GLOBAL') {
        vm.dataObj.countryCode = '';
      }
      
      vm.isViewMode = false; // Set to edit mode
      console.log(vm.dataObj, 'datattt');
      vm.modalRef = vm._modal.open(this.content, { backdrop: 'static', size: 'xl' });
    });
  }

  setContent(c: any) {
    this.content = c;
  }

  onSubmit(flag: any) {
    // Prepare the data for submission
    this.dataObj['countryCode'] = this.dataObj.countryCode || 'GLOBAL';
    this.dataObj['priority'] = this.dataObj.priority || 1;
    this.dataObj['isActive'] = this.dataObj.isActive || false;
    
    // Convert config from string to object if it's a string
    if (typeof this.dataObj.config === 'string') {
      try {
        // Handle empty string case
        if (!this.dataObj.config.trim()) {
          this.dataObj.config = {};
        } else {
          this.dataObj.config = JSON.parse(this.dataObj.config);
        }
      } catch (e) {
        console.error('Invalid JSON configuration', e);
        alert('Invalid JSON configuration format: ' + e.message);
        return;
      }
    }
    
    console.log(this.dataObj);
    
    const apiUrl = this.dataObj._id ? `/admin/providers/${this.dataObj._id}` : '/admin/providers';
    const method = this.dataObj._id ? 'put' : 'post';
    
    if (method === 'post') {
      this._api.postData(apiUrl, this.dataObj).subscribe((res: any) => {
        console.log(res);
        if (res && !res.error) {
          alert('Provider created successfully');
          this.onClose();
          this.gridApi.refreshInfiniteCache();
        } else {
          console.error('Error creating provider:', res);
          alert('Error creating provider: ' + (res.message || 'Unknown error'));
        }
      }, (error) => {
        console.error('HTTP Error creating provider:', error);
        alert('HTTP Error creating provider: ' + error);
      });
    } else {
      this._api.putData(apiUrl, this.dataObj).subscribe((res: any) => {
        console.log(res);
        if (res && !res.error) {
          alert('Provider updated successfully');
          this.onClose();
          this.gridApi.refreshInfiniteCache();
        } else {
          console.error('Error updating provider:', res);
          alert('Error updating provider: ' + (res.message || 'Unknown error'));
        }
      }, (error) => {
        console.error('HTTP Error updating provider:', error);
        alert('HTTP Error updating provider: ' + error);
      });
    }
  }

  onClose() {
    this.modalRef.close();
  }

  toggleStatus(data: any) {
    const vm = this;
    const updatedData = {
      isActive: !data.isActive
    };
    
    vm._api.putData(`/admin/providers/${data._id}`, updatedData).subscribe((res: any) => {
      if (res && !res.error) {
        alert('Status updated successfully');
        vm.gridApi.refreshInfiniteCache();
      } else {
        console.error('Error updating status:', res);
        alert('Error updating status: ' + (res.message || 'Unknown error'));
      }
    }, (error) => {
      console.error('HTTP Error updating status:', error);
      alert('HTTP Error updating status: ' + error);
    });
  }
  
  onViewDetails(params: any) {
    const vm = this;
    vm.zone.run(() => {
      vm.dataObj = { ...params.data }; // Create a copy to avoid direct mutation
      
      // Handle country code mapping for view mode - if countryCode is "GLOBAL" or falsy, show as "GLOBAL"
      if (!vm.dataObj.countryCode || vm.dataObj.countryCode === 'GLOBAL') {
        vm.dataObj.countryCode = 'GLOBAL';
      }
      
      vm.isViewMode = true; // Set to view mode
      // Open the same modal in read-only mode
      vm.modalRef = vm._modal.open(this.content, { backdrop: 'static', size: 'xl' });
    });
  }
}